import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Chip,
  Box,
  useTheme,
  useMediaQuery,
  alpha,
} from "@mui/material";

import { tokens } from "../../theme";
import useDebounce from "../../hooks/useDebounce";
import { getUnallocatedPayments } from "../../redux/actions/unallocatedPaymentActions";
import StandardTable, { TableColumn } from "../common/StandardTable";
import DateRangeFilter from "../common/DateRangeFilter";

import { useAppDispatch, useAppSelector } from "../../redux/store";
import { UnallocatedPaymentResponseDto } from "../../shared/dtos/unallocatedPayment/UnallocatedPaymentResponseDto";

interface AllocatePaymentModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (payment: UnallocatedPaymentResponseDto) => void;
}

const AllocatePaymentModal: React.FC<AllocatePaymentModalProps> = ({
  open,
  onClose,
  onSelect,
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    items: unallocatedPayments,
    loading: loadingUnallocated,
    error: errorLoadingUnallocated,
    pagination,
  } = useAppSelector((state) => state.unallocatedPayment);

  const today = new Date().toISOString().split("T")[0];
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);
  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const fetchPayments = useCallback(() => {
    dispatch(
      getUnallocatedPayments({
        page,
        size: rowsPerPage,
        search: debouncedSearch,
        startDate,
        endDate,
      }),
    );
  }, [dispatch, page, rowsPerPage, debouncedSearch, startDate, endDate]);

  useEffect(() => {
    if (open) fetchPayments();
  }, [open, fetchPayments]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, startDate, endDate]);

  const columns: TableColumn<UnallocatedPaymentResponseDto>[] = [
    {
      id: "createdAt",
      label: "Date",
      format: (_, row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {new Date(row.createdAt).toLocaleDateString()}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {new Date(row.createdAt).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </Typography>
        </Box>
      ),
    },
    {
      id: "gatewayReceiptNumber",
      label: "Reference",
      format: (_, row) => (
        <Chip
          label={row.externalReference}
          size="small"
          sx={{
            fontWeight: "bold",
            bgcolor: alpha(theme.palette.secondary.main, 0.1),
            color: "secondary.main",
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
          }}
        />
      ),
    },
    {
      id: "studentName",
      label: "Student",
      format: (_, row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {row.studentName || "N/A"}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {row.studentId || "No ID"}
          </Typography>
        </Box>
      ),
    },
    {
      id: "amount",
      label: "Amount",
      align: "right",
      format: (_, row) => (
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: "secondary.main" }}
        >
          {Number(row.amount).toFixed(2)}
        </Typography>
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      slotProps={{
        paper: {
          sx: {
            bgcolor: colors.primary[400],
            backgroundImage: "none",
          },
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: "bold", pb: 1 }}>
        Allocate Electronic Payment
      </DialogTitle>

      <DialogContent sx={{ p: isMobile ? 1 : 3 }}>
        {/* --- Highlighted Filter Box --- */}
        <Box sx={{ mb: 3, p: 2 }}>
          <Stack
            sx={{
              flexDirection: isMobile ? "column" : "row",
              spacing: 2,
              alignItems: isMobile ? "stretch" : "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <TextField
              label="Search Ref / Phone"
              size="small"
              sx={{
                width: isMobile ? "100%" : 320,
                "& .MuiOutlinedInput-root": { bgcolor: colors.primary[400] },
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by reference or phone..."
            />

            <DateRangeFilter
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
            />
          </Stack>
        </Box>

        {/* --- Standard Table with explicit container styles --- */}
        <Box>
          <StandardTable
            columns={columns}
            data={unallocatedPayments}
            loading={loadingUnallocated}
            error={errorLoadingUnallocated}
            onRetry={fetchPayments}
            page={page}
            count={pagination?.totalElements || 0}
            rowsPerPage={rowsPerPage}
            onPageChange={(newPage) => setPage(newPage)}
            onRowsPerPageChange={(newSize) => {
              setRowsPerPage(newSize);
              setPage(1);
            }}
            rowAction={(row) => (
              <Button
                size="small"
                variant="contained"
                color="secondary"
                sx={{ fontWeight: "bold", textTransform: "none" }}
                onClick={() => onSelect(row)}
              >
                Link
              </Button>
            )}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ p: 2, borderTop: `1px solid ${colors.primary[300]}` }}
      >
        <Button onClick={onClose} sx={{ color: colors.grey[100] }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AllocatePaymentModal;
